const collectionHandle = window.location.pathname.match(/^\/collections\/([^/]+)\/?$/)?.[1];
if (['current-releases', 'past-vintages'].includes(collectionHandle)) {
  document.body.classList.add(`collection-${collectionHandle}`);
}

class WolfCubMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    this.button?.addEventListener('click', () => this.toggle());
    this.querySelectorAll('[data-menu-close]').forEach((node) => node.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.close());
  }
  toggle() { this.hasAttribute('open') ? this.close() : this.open(); }
  open() { this.setAttribute('open', ''); this.button?.setAttribute('aria-expanded', 'true'); document.documentElement.classList.add('scroll-locked'); }
  close() { this.removeAttribute('open'); this.button?.setAttribute('aria-expanded', 'false'); document.documentElement.classList.remove('scroll-locked'); }
}
customElements.define('wolf-cub-menu', WolfCubMenu);

class WolfCubAgeGate extends HTMLElement {
  connectedCallback() {
    if (this.dataset.designMode === 'true') return;
    const remembered = this.dataset.remember === 'true' && window.localStorage.getItem('wolf-cub-age-confirmed') === 'yes';
    if (!remembered) this.showModal();
    this.querySelector('[data-age-yes]')?.addEventListener('click', () => {
      if (this.dataset.remember === 'true') window.localStorage.setItem('wolf-cub-age-confirmed', 'yes');
      this.close();
    });
    this.querySelector('[data-age-no]')?.addEventListener('click', () => {
      const exit = this.dataset.exitUrl || 'https://www.google.com/';
      window.location.assign(exit);
    });
  }
  showModal() { this.setAttribute('open', ''); this.querySelector('dialog')?.showModal(); document.documentElement.classList.add('scroll-locked'); }
  close() { this.querySelector('dialog')?.close(); this.removeAttribute('open'); document.documentElement.classList.remove('scroll-locked'); }
}
customElements.define('wolf-cub-age-gate', WolfCubAgeGate);

class WolfCubCaseBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.caseBuilderReady === 'true') return;
    this.dataset.caseBuilderReady = 'true';
    this.target = this.dataset.initialTarget === '12' ? 12 : 6;
    this.currency = this.dataset.currency || 'AUD';
    this.wines = [...this.querySelectorAll('[data-case-wine]')];
    this.addButton = this.querySelector('[data-case-add]');
    this.error = this.querySelector('[data-case-error]');
    if (!this.addButton || this.wines.length === 0) return;
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2
    });

    this.addEventListener('click', (event) => {
      const targetButton = event.target.closest('[data-case-target]');
      if (targetButton) {
        this.chooseTarget(Number(targetButton.dataset.caseTarget));
        return;
      }

      const adjustButton = event.target.closest('[data-case-adjust]');
      if (adjustButton) {
        this.adjustWine(adjustButton, Number(adjustButton.dataset.caseAdjust));
        return;
      }

      if (event.target.closest('[data-case-add]')) this.addCase();
    });

    this.update();
  }

  totalQuantity() {
    return this.wines.reduce((total, wine) => total + Number(wine.dataset.quantity || 0), 0);
  }

  subtotal() {
    return this.wines.reduce((total, wine) => {
      return total + (Number(wine.dataset.price || 0) * Number(wine.dataset.quantity || 0));
    }, 0);
  }

  chooseTarget(nextTarget) {
    if (![6, 12].includes(nextTarget)) return;
    if (nextTarget === 6 && this.totalQuantity() > 6) {
      this.showError('Reduce your selection to six bottles before choosing the smaller case.');
      return;
    }
    this.target = nextTarget;
    this.hideError();
    this.update();
  }

  adjustWine(button, change) {
    const wine = button.closest('[data-case-wine]');
    if (!wine || ![-1, 1].includes(change)) return;

    const current = Number(wine.dataset.quantity || 0);
    const maximum = Math.max(0, Number(wine.dataset.max || 12));
    const selected = this.totalQuantity();

    if (change > 0 && selected >= this.target) {
      if (this.target === 6 && selected < 12) this.target = 12;
      else return;
    }

    const next = Math.min(maximum, Math.max(0, current + change));
    if (next === current) return;

    wine.dataset.quantity = String(next);
    this.hideError();
    this.update();
  }

  update() {
    const selected = this.totalQuantity();
    const remaining = Math.max(0, this.target - selected);
    const ready = selected === this.target;
    const discount = this.target === 12 ? Number(this.dataset.twelveDiscount || 0) : Number(this.dataset.sixDiscount || 0);
    const subtotal = this.subtotal();
    const saving = Math.round(subtotal * (discount / 100));

    this.querySelectorAll('[data-case-target]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.caseTarget) === this.target));
    });

    this.wines.forEach((wine) => {
      const quantity = Number(wine.dataset.quantity || 0);
      const maximum = Math.max(0, Number(wine.dataset.max || 12));
      wine.querySelector('[data-case-quantity]').textContent = String(quantity);
      wine.querySelector('[data-case-adjust="-1"]').disabled = quantity === 0;
      wine.querySelector('[data-case-adjust="1"]').disabled = quantity >= maximum || selected >= 12;
      wine.toggleAttribute('data-selected', quantity > 0);
    });

    this.querySelector('[data-case-count]').textContent = String(selected);
    this.querySelector('[data-case-target-count]').textContent = String(this.target);
    this.querySelector('[data-case-progress]').style.width = `${Math.min(100, (selected / this.target) * 100)}%`;
    this.querySelector('[data-case-subtotal]').textContent = this.money.format(subtotal / 100);
    this.querySelector('[data-case-saving]').textContent = ready ? `${this.money.format(saving / 100)} (${discount}%)` : `${discount}% when complete`;
    this.querySelector('[data-case-total]').textContent = ready ? this.money.format((subtotal - saving) / 100) : '—';

    const status = this.querySelector('[data-case-status]');
    if (selected === 0) status.textContent = 'Choose your first bottle.';
    else if (ready) status.textContent = `Your ${this.target}-bottle case is ready.`;
    else status.textContent = `${remaining} more ${remaining === 1 ? 'bottle' : 'bottles'} to complete your case.`;

    this.addButton.disabled = !ready;
    this.addButton.textContent = ready ? `Add ${this.target} bottles to cart` : `Select ${remaining} more`;
  }

  async addCase() {
    if (this.totalQuantity() !== this.target || this.addButton.disabled) return;

    const items = this.wines
      .map((wine) => ({ id: wine.dataset.variantId, quantity: Number(wine.dataset.quantity || 0) }))
      .filter((item) => item.quantity > 0);

    this.addButton.disabled = true;
    this.addButton.textContent = 'Adding your case…';
    this.hideError();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'One or more wines could not be added.');
      }

      window.location.assign(this.dataset.cartUrl || `${root}cart`);
    } catch (error) {
      this.showError(error.message || 'Your case could not be added. Please try again.');
      this.update();
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  hideError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.hidden = true;
  }
}
customElements.define('wolf-cub-case-builder', WolfCubCaseBuilder);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quantity-choice]');
  if (!trigger) return;
  const form = trigger.closest('form');
  const input = form?.querySelector('input[name="quantity"]');
  if (!input) return;
  input.value = trigger.dataset.quantityChoice;
  form.querySelectorAll('[data-quantity-choice]').forEach((button) => button.setAttribute('aria-pressed', button === trigger ? 'true' : 'false'));
});

// WOLF&CUB interactive case builder.
